import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ExternalLink, AlertTriangle, ShieldCheck } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { FAQ } from "@/components/site/article/FAQ";

const SITE = "https://xauproacademy.com";
const URL = `${SITE}/best-prop-firms-for-gold`;
const TITLE = "Best Prop Firms for Gold: Rules Compared";
const DESC =
  "Compare leading prop firms for gold trading: Trustpilot profile, years operating, drawdown model, XAUUSD leverage, news and weekend rules.";

interface Firm {
  name: string;
  since: number;
  drawdown: string;
  goldLeverage: string;
  news: string;
  weekend: string;
  trustpilot: string;
}

const FIRMS: Firm[] = [
  { name: "FTMO", since: 2015, drawdown: "Static (balance based)", goldLeverage: "Up to 1:30 on metals (varies by account)", news: "Restricted on standard funded accounts", weekend: "Swing account only", trustpilot: "https://www.trustpilot.com/review/ftmo.com" },
  { name: "The5ers", since: 2016, drawdown: "Static on most programs", goldLeverage: "Program dependent", news: "Program dependent", weekend: "Allowed on most programs", trustpilot: "https://www.trustpilot.com/review/the5ers.com" },
  { name: "Alpha Capital Group", since: 2021, drawdown: "Static, daily limit from balance", goldLeverage: "Program dependent", news: "Restrictions around high impact events", weekend: "Program dependent", trustpilot: "https://www.trustpilot.com/review/alphacapitalgroup.uk" },
  { name: "Funding Pips", since: 2022, drawdown: "Static, daily from balance or equity", goldLeverage: "Program dependent", news: "Restrictions on funded stage", weekend: "Program dependent", trustpilot: "https://www.trustpilot.com/review/fundingpips.com" },
  { name: "FundedNext", since: 2022, drawdown: "Static on evaluation models", goldLeverage: "Program dependent", news: "Program dependent", weekend: "Program dependent", trustpilot: "https://www.trustpilot.com/review/fundednext.com" },
];

const FAQS = [
  { q: "Which drawdown model suits gold traders?", a: "A static, balance based drawdown. Gold routinely moves 20 to 40 USD intraday, and a trailing model locks in open profit as your new floor, so a normal retracement can breach it." },
  { q: "Can you hold gold trades over the weekend on prop firms?", a: "Only on accounts that explicitly allow it, often labelled swing. Gold can gap at the Sunday open, so weekend exposure should be sized as if the stop may be skipped." },
  { q: "Why does trailing drawdown hurt gold traders?", a: "Gold's volatility means open profit frequently gives back part of its move. A trailing limit tightens with each new equity high, converting ordinary pullbacks into rule breaches." },
  { q: "What lot size is reasonable on gold for a 100k account?", a: "At 0.5 percent risk with a 20 USD stop, about 0.25 lots on a 100 oz contract. Use the position size calculator to size from your stop and daily loss limit." },
  { q: "Are Trustpilot scores enough to choose a firm?", a: "No. Use them as one signal alongside years operating, payout terms and written gold rules. Scores change, so always open the live profile before buying a challenge." },
];

export const Route = createFileRoute("/best-prop-firms-for-gold")({
  head: () => ({
    meta: [
      { title: `${TITLE} | XAU Pro` },
      { name: "description", content: DESC },
      { name: "keywords", content: "best prop firms for gold, best prop firm for xauusd, funded account gold trading, prop firm gold rules, gold prop firm comparison" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Prop Firm Gold", item: `${SITE}/prop-firm-gold` },
            { "@type": "ListItem", position: 3, name: "Best Prop Firms for Gold", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <Layout>
      <section className="container mx-auto max-w-6xl px-4 py-14">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Prop firm comparison</div>
        <h1 className="mt-3 font-display text-3xl font-semibold md:text-5xl">Best Prop Firms for Gold Trading</h1>
        <p className="mt-4 max-w-3xl text-muted-foreground">
          A gold specific view of established prop firms. Each firm is read through the rules that decide XAUUSD
          outcomes: drawdown model, metals leverage, news restrictions and weekend holding. We receive no compensation
          from any firm listed.
        </p>

        <div className="mt-10 overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-card/60 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                {["Firm", "Operating since", "Drawdown model", "Gold leverage", "News trading", "Weekend holding", "Trustpilot"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FIRMS.map((f) => (
                <tr key={f.name} className="border-t border-border/60">
                  <td className="px-4 py-3 font-semibold">{f.name}</td>
                  <td className="px-4 py-3 font-mono">{f.since}</td>
                  <td className="px-4 py-3 text-muted-foreground">{f.drawdown}</td>
                  <td className="px-4 py-3 text-muted-foreground">{f.goldLeverage}</td>
                  <td className="px-4 py-3 text-muted-foreground">{f.news}</td>
                  <td className="px-4 py-3 text-muted-foreground">{f.weekend}</td>
                  <td className="px-4 py-3">
                    <a href={f.trustpilot} target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-1 text-gold hover:underline">
                      Live rating <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Rules change frequently and differ by program. Confirm current terms on each firm's website before purchase.
          Trustpilot links open the live public profile so the score you see is always current.
        </p>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <div className="rounded-xl border border-border/60 bg-card/60 p-6">
            <AlertTriangle className="h-5 w-5 text-gold" />
            <h2 className="mt-3 font-display text-xl font-semibold">Gold specific traps</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
              <li>Trailing drawdown converts normal gold retracements into breaches.</li>
              <li>Spread widening around CPI, NFP and FOMC can trigger stops and daily limits.</li>
              <li>Sunday open gaps bypass stop orders on held positions.</li>
              <li>Lot sizing copied from forex overstates gold risk by a wide margin.</li>
            </ul>
          </div>
          <div className="rounded-xl border border-border/60 bg-card/60 p-6">
            <ShieldCheck className="h-5 w-5 text-gold" />
            <h2 className="mt-3 font-display text-xl font-semibold">Selection criteria</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
              <li>Static, balance based drawdown.</li>
              <li>Written metals rules, including news windows.</li>
              <li>Multi year operating history and consistent payouts.</li>
              <li>Risk per trade at or below 0.5 to 1 percent of balance.</li>
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild><Link to="/position-size-calculator">Size a gold position <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
          <Button asChild variant="outline"><Link to="/prop-firm-gold">Prop firm gold trading guide</Link></Button>
        </div>

        <FAQ items={FAQS} />
      </section>
    </Layout>
  );
}
